import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { hasActiveSession, redirectSignedInUser } from "../utils/authRedirect";

/**
 * Wraps the login and sign-up pages. Someone who is already signed in never
 * sees them (a half-working form with a disabled button, as QA found); they go
 * straight to the site that asked for the login, or home -- including when the
 * Indexx ID session only arrives after the page has opened. ?action=Logout is
 * let through so the login page can still sign people out.
 */
const RedirectIfSignedIn = ({ children }: { children: React.ReactElement }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const loggingOut = params.get("action") === "Logout";
  const [signedIn, setSignedIn] = useState(() => !loggingOut && hasActiveSession());

  useEffect(() => {
    if (signedIn) redirectSignedInUser(params.get("redirectWebsiteLink"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  /*
   * …and again once the session arrives.
   *
   * Someone who is already signed in elsewhere in the estate lands here with
   * no `access_token`: the Indexx ID launcher's silent check writes it a round
   * trip later and fires "zodiac:session". Without this they would be shown a
   * login/sign-up form they do not need instead of being forwarded (with a
   * shortToken) to Lotto, the Exchange, Indexx Pay or Bitcoin Yay. Routes pass
   * reloadOnSession:false (LandingHeader.tsx), so this event is the signal.
   */
  useEffect(() => {
    if (loggingOut || signedIn) return;
    const onEstateSession = () => {
      if (hasActiveSession()) setSignedIn(true);
    };
    window.addEventListener("zodiac:session", onEstateSession);
    return () => window.removeEventListener("zodiac:session", onEstateSession);
  }, [loggingOut, signedIn]);

  if (!signedIn) return children;
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
      }}
    >
      <CircularProgress size={28} />
      <p style={{ fontSize: 16 }}>You're already signed in. Taking you there…</p>
    </div>
  );
};

export default RedirectIfSignedIn;
